import { Box, Text } from "@modernatx/ui-kit-react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import {
  Appearance,
  loadStripe,
  Stripe,
  StripePaymentElementOptions
} from "@stripe/stripe-js";
import { AxiosError } from "axios";
import React from "react";

import { useStripePaymentSetup } from "@/hooks/useStripePaymentSetup";
import { PaymentMethod } from "@/types/PaymentMethod";
import { getErrorMessage } from "@/utils/getErrorMessage";
import {
  generateIdempotencyToken,
  PAYMENT_METHOD_TYPES,
  PAYMENT_PROVIDER
} from "@/utils/InvoicePay";
import {
  describePaymentMethodApi,
  updatePaymentMethodApi
} from "@/utils/PaymentsService/PaymentMethodApis";

import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import {
  CREDIT_CARD_MANDATE_TEXT,
  INVOICE_PAY_ACH_MANDATE_TEXT
} from "../Payments/PaymentMethodForm";

const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.STRIPE_PUBLISHABLE_KEY!,
  { betas: ["blocked_card_brands_beta_2"] }
);

const appearance: Appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#0570de",
    colorBackground: "#ffffff",
    colorText: "#30313d",
    colorDanger: "#df1b41",
    fontFamily: "Ideal Sans, system-ui, sans-serif",
    spacingUnit: "4px",
    borderRadius: "4px"
  }
};

export const PaymentForm: React.FC<{
  setSubmitRef: (submitFn: () => Promise<PaymentMethod | undefined>) => void;
  source?: "modal" | "fullscreen";
}> = ({ setSubmitRef, source }) => {
  const { clientSecret, paymentMethodId } =
    useStripePaymentSetup(PAYMENT_PROVIDER);

  if (!clientSecret) return null;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <StripePaymentForm
        paymentMethodId={paymentMethodId}
        setSubmitRef={setSubmitRef}
        source={source}
      />
    </Elements>
  );
};

const StripePaymentForm: React.FC<{
  paymentMethodId: string;
  setSubmitRef: (submitFn: () => Promise<PaymentMethod | undefined>) => void;
  source?: "modal" | "fullscreen";
}> = ({ paymentMethodId, setSubmitRef, source }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethodType, setPaymentMethodType] = React.useState<string>();
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const paymentElementOptions: StripePaymentElementOptions = {
    paymentMethodOrder: [PAYMENT_METHOD_TYPES.ACH],
    terms: {
      card: "never",
      usBankAccount: "never"
    }
  };

  const handlePaymentMethodTypeChange = (event: {
    value: { type: React.SetStateAction<string | undefined> };
  }) => {
    setPaymentMethodType(event.value.type);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: {},
      redirect: "if_required"
    });

    if (error) {
      setErrorMessage(error.message);
      return;
    }
    setIsSubmitting(true);

    if (setupIntent) {
      try {
        const updatePaymentMethodRequest = {
          idempotencyToken: generateIdempotencyToken(),
          paymentMethodLifecycleDetails: {
            externalPaymentMethodId: setupIntent?.payment_method
          }
        };
        const response = await updatePaymentMethodApi(
          updatePaymentMethodRequest,
          paymentMethodId
        );
        if (response?.status === 200) {
          const newPaymentMethodId = response?.data?.paymentMethodId;
          const newPaymentMethod =
            await describePaymentMethodApi(newPaymentMethodId);
          let newPaymentMethodDetails;
          if (newPaymentMethod?.status === 200) {
            newPaymentMethodDetails =
              newPaymentMethod?.data?.paymentMethodDetails;
          }
          return newPaymentMethodDetails;
        }
      } catch (err: unknown) {
        if (err instanceof AxiosError && err.response?.data.errorCode) {
          const message = getErrorMessage(err?.response?.data?.errorCode);
          setErrorMessage(message);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrorMessage(
        "Something went wrong, please refresh the page and try again."
      );
    }
  };

  React.useEffect(() => {
    setSubmitRef(handleSubmit);
  });

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {isSubmitting && source === "modal" && (
          <LoadingSpinner
            size="md"
            message="Saving payment method..."
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "radius-2"
            }}
          />
        )}

        <form onSubmit={handleSubmit}>
          <PaymentElement
            options={paymentElementOptions}
            onChange={handlePaymentMethodTypeChange}
          />
        </form>
      </Box>
      <Text
        sx={{ color: "color-system-gray-400", pt: "4", textAlign: "justify" }}
      >
        {paymentMethodType === PAYMENT_METHOD_TYPES.ACH
          ? INVOICE_PAY_ACH_MANDATE_TEXT
          : CREDIT_CARD_MANDATE_TEXT}
      </Text>
      {errorMessage && (
        <Text sx={{ color: "color-system-red-700" }}>{errorMessage}</Text>
      )}
    </>
  );
};
